
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../products/utils/currencyUtils';
import { Product } from '../products/types';
import { Service } from '../types';

interface SimilarItemsProps {
  type: 'product' | 'service';
  currentItemId: string;
  category?: string;
  limit?: number;
}

export const SimilarItems: React.FC<SimilarItemsProps> = ({
  type,
  currentItemId,
  category,
  limit = 4
}) => {
  const navigate = useNavigate();

  const isProduct = type === 'product';
  const tableName = isProduct ? 'professional_products' : 'professional_services';
  const nameField = isProduct ? 'title' : 'service_name';
  
  const { data: items, isLoading } = useQuery({
    queryKey: [`similar-${type}s`, currentItemId, category],
    queryFn: async () => {
      let query = supabase
        .from(tableName)
        .select(`
          *,
          profiles:professional_id (
            full_name,
            profile_photo
          )
        `)
        .neq('id', currentItemId)
        .limit(limit);
        
      if (category) {
        if (isProduct) {
          // For products, we might filter by condition or another appropriate field
          query = query.ilike('category', `%${category}%`);
        } else {
          // For services, filter by category
          query = query.ilike('category', `%${category}%`);
        }
      }
        
      const { data, error } = await query;
        
      if (error) throw error;
      return data || [];
    },
  });

  if (isLoading || !items || items.length === 0) {
    return null;
  }

  const handleItemClick = (item: any) => {
    const route = isProduct 
      ? `/services/products/${item.id}`
      : `/service/${item.id}`;
      
    navigate(route);
  };

  return (
    <div className="mt-4">
      <h3 className="text-lg font-medium mb-3">
        {isProduct ? 'Produits similaires' : 'Services similaires'}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {items.map((item: any) => (
          <Card 
            key={item.id}
            className="overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer"
            onClick={() => handleItemClick(item)}
          >
            <div className="relative aspect-[4/3] w-full overflow-hidden">
              {item.photo_url ? (
                <img
                  src={item.photo_url}
                  alt={item[nameField]}
                  className="w-full h-full object-cover hover:scale-105 transition-transform"
                />
              ) : (
                <div className="w-full h-full bg-muted/10 flex items-center justify-center">
                  <span className="text-[10px] text-muted-foreground">No image</span>
                </div>
              )}
              <div className="absolute bottom-1 right-1 bg-primary/90 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-primary-foreground">
                {isProduct 
                  ? formatPrice((item as Product).price, (item as Product).currency)
                  : `${(item as Service).price}€`}
              </div>
            </div>
            <div className="p-2">
              <h4 className="font-medium text-xs truncate">{item[nameField]}</h4>
              <p className="text-[10px] text-muted-foreground truncate">{item.description}</p>
              {item.profiles && (
                <div className="mt-1 text-[10px] text-muted-foreground flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-primary/10 flex-shrink-0"></div>
                  <span className="truncate">{item.profiles.full_name}</span>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
